/* === AgAvatar & AgAvatarGroup — Anagogia Design System === */

import { cloneElement, isValidElement, useState } from 'react';
import styles from './AgAvatar.module.css';

/* ── AgAvatar ── */

export interface AgAvatarProps {
  /** Image source URL */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Fallback text (initials) when no image */
  children?: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'circular' | 'rounded' | 'square';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
}

const COLOR_MAP: Record<string, string> = {
  primary: styles.colorPrimary,
  secondary: styles.colorSecondary,
  success: styles.colorSuccess,
  warning: styles.colorWarning,
  error: styles.colorError,
  info: styles.colorInfo,
};

const VARIANT_MAP: Record<string, string | undefined> = {
  circular: undefined,
  rounded: styles.rounded,
  square: styles.square,
};

function getInitials(alt?: string): string {
  if (!alt) return '?';
  const words = alt.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

/**
 * Avatar utilisateur : image, initiales ou icône.
 * @category Affichage
 */
function AgAvatar({
  src,
  alt,
  children,
  size = 'md',
  variant = 'circular',
  color = 'primary',
  className,
  style,
  onClick,
}: AgAvatarProps) {
  const [imgError, setImgError] = useState(false);

  const showImage = src && !imgError;

  const cls = [
    styles.avatar,
    styles[size],
    VARIANT_MAP[variant],
    !showImage ? COLOR_MAP[color] : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cls}
      style={style}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {showImage ? (
        <img
          className={styles.image}
          src={src}
          alt={alt || ''}
          onError={() => setImgError(true)}
        />
      ) : (
        children || getInitials(alt)
      )}
    </div>
  );
}

/* ── AgAvatarGroup ── */

export interface AgAvatarGroupProps {
  /** Maximum number of avatars to show */
  max?: number;
  /** Size applied to all children */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  children: React.ReactNode;
}

/**
 * Groupe d'avatars empilés avec indicateur de débordement.
 * @category Affichage
 */
function AgAvatarGroup({
  max = 5,
  size,
  className,
  children,
}: AgAvatarGroupProps) {
  const childArray = Array.isArray(children) ? children : [children];
  const total = childArray.length;
  const overflow = total > max ? total - max : 0;
  const visible = overflow > 0 ? childArray.slice(0, max) : childArray;

  const cls = [styles.group, className].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      {overflow > 0 && (
        <div className={[styles.avatar, styles[size || 'md'], styles.overflow].filter(Boolean).join(' ')}>
          +{overflow}
        </div>
      )}
      {[...visible].reverse().map((child, i) => {
        if (!isValidElement<AgAvatarProps>(child)) return child;
        // `key` ne transite plus par `props` depuis React 19 : le lire sur l'élément
        const key = child.key ?? i;
        return size ? cloneElement(child, { key, size }) : cloneElement(child, { key });
      })}
    </div>
  );
}

export { AgAvatar, AgAvatarGroup };
export default AgAvatar;
